import { ColorBlock } from "@/components/colorblock/ColorBlock.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { LabelEditModal } from "@/pages/settings/recruit/labels/widgets/LabelEditModal.tsx";
import { useApplicantsUpdateLabels } from "@/queries/recruit/use-applicants.query.ts";
import { useLabels } from "@/queries/use-labels.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiApplicant } from "@/types/recruit/applicant.types.ts";
import { ApiLabel } from "@/types/recruit/label.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Tag,
  toCssColorValue,
  useModals,
} from "@skailer-com/uikit";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export const ApplicantLabels = ({ applicant }: { applicant: ApiApplicant }) => {
  const { t } = useTranslation();
  const modalsService = useModals();
  const { canIEdit } = usePermissions();

  const canManageCandidate = canIEdit(ApiPermission.manage_candidates);
  const { data: labels } = useLabels(canManageCandidate || false);
  const applicantUpdateLabels = useApplicantsUpdateLabels();

  const handleToggleLabel = useCallback(
    async (label: ApiLabel) => {
      const currentLabelIds = applicant.labels.map((labelId) => labelId.id);
      const newLabelIds = currentLabelIds.find((x) => x == label.id)
        ? currentLabelIds.filter((x) => x != label.id)
        : [...currentLabelIds, label.id];

      applicantUpdateLabels.mutate(
        {
          applicantId: applicant.id,
          labelIds: newLabelIds,
        },
        {
          onSuccess: () => {
            analyticsService.trackEvent(analyticEvents.applicants.labelsEdited, {
              [analyticProperties.id]: applicant.id,
              [analyticProperties.title]: label.title,
            });
          },
        }
      );
    },
    [applicant]
  );

  const handleCreateLabel = useCallback(() => {
    const labelToEdit = {
      id: 0,
      title: "",
      color: "",
    };

    modalsService.openModal(LabelEditModal, {
      label: labelToEdit,
    });
  }, []);

  return (
    <Box>
      <Stack direction={"horizontal"} gap={"sm"}>
        {applicant.labels.map((label, index) => (
          <Tag color={toCssColorValue(label.color)} key={`applicant-tag-${index}`}>
            {label.title}
          </Tag>
        ))}
        {canManageCandidate && (
          <DropdownMenu
            closeOnClick={false}
            trigger={
              <Button
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                variant={"secondary"}
              >
                {applicant.labels.length == 0 ? t("ats:tag_add") : ""}
              </Button>
            }
          >
            <Stack gap={"sm"}>
              <Button variant={"secondary"} onClick={handleCreateLabel}>
                {t("common:create_new")}
              </Button>
            </Stack>
            {labels?.map((label) => (
              <DropdownMenuItem
                key={`label-${label.id}`}
                leftIcon={<ColorBlock color={label.color} />}
                onClick={() => {
                  handleToggleLabel(label);
                }}
                title={label.title}
              />
            ))}
          </DropdownMenu>
        )}
      </Stack>
    </Box>
  );
};
